import moment from 'moment'
import { useCallback, useEffect, useReducer, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import ErrorIcon from '@material-ui/icons/Error'
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled'
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled'
import SearchIcon from '@material-ui/icons/Search'

import ConfirmationModal from '../../components/ConfirmationModal'
import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'
import ReminderMessagesModal from '../../components/ReminderMessagesModal'
import ReminderModal from '../../components/ReminderModal'

import {
  BellRing,
  CirclePause,
  CirclePlay,
  Settings,
  Trash2
} from 'lucide-react'
import { ButtonSecondary } from '../../components/ButtonSecondary'
import { InputFieldText } from '../../components/InputFieldText'
import SelectInput from '../../components/SelectInput'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { useStyles } from './style'
import QuickTable from '../../components/QuickTable'

const reducer = (state, action) => {
  if (action.type === 'LOAD_REMINDERS') {
    const reminders = action.payload
    const newReminders = []

    reminders.forEach(reminder => {
      const reminderIndex = state.findIndex(s => s.id === reminder.id)
      if (reminderIndex !== -1) {
        state[reminderIndex] = reminder
      } else {
        newReminders.push(reminder)
      }
    })

    return [...state, ...newReminders]
  }

  if (action.type === 'UPDATE_REMINDERS') {
    const reminder = action.payload
    const reminderIndex = state.findIndex(s => s.id === reminder.id)

    if (reminderIndex !== -1) {
      state[reminderIndex] = reminder
      return [...state]
    } else {
      return [reminder, ...state]
    }
  }

  if (action.type === 'DELETE_REMINDER') {
    const reminderId = action.payload
    const reminderIndex = state.findIndex(s => s.id === reminderId)
    if (reminderIndex !== -1) {
      state.splice(reminderIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const Reminders = () => {
  const classes = useStyles()
  // const { user } = useContext(AuthContext)

  const [loading, setLoading] = useState(false)
  const [, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [selectedReminder, setSelectedReminder] = useState(null)
  const [deletingReminder, setDeletingReminder] = useState(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [reminders, dispatch] = useReducer(reducer, [])
  const [reminderModalOpen, setReminderModalOpen] = useState(false)
  const [messagesModalOpen, setMessagesModalOpen] = useState(false)

  // Filtros
  const [statusFilter, setStatusFilter] = useState('')
  const [documentTypeFilter, setDocumentTypeFilter] = useState('')
  const [startDateFilter, setStartDateFilter] = useState('')
  const [endDateFilter, setEndDateFilter] = useState('')

  const columns = [
    {
      field: 'cliente.nome',
      headerName: 'Cliente',
      customValue: r => r.cliente?.nome || r.empresa || '-'
    },
    { field: 'responsible.name', headerName: 'Responsável' },
    {
      field: 'contact',
      headerName: 'Contato',
      customValue: r => r.contact?.name || r.contact?.number || '-'
    },
    { field: 'documentType', headerName: 'Tipo de Documento' },
    {
      field: 'nextExecution',
      headerName: 'Próxima Execução'
    },
    {
      field: 'dueDate',
      headerName: 'Vencimento'
    },
    {
      field: 'status',
      headerName: 'Status',
      align: 'center'
    }
  ]

  const documentTypes = [
    'Simples Nacional',
    'Certificado Digital',
    'Whatsapp Empresa',
    'Lista Natal',
    'IRPF',
    'IRPJ',
    'DCTF',
    'EFD Contribuições',
    'SPED Fiscal',
    'SPED Contábil',
    'FGTS',
    'GPS',
    'INSS',
    'Outros'
  ]

  const statusOptions = [
    { value: 'pendente', label: 'Pendente', color: '#ff9800' },
    { value: 'em_andamento', label: 'Em Andamento', color: '#ffc107' },
    { value: 'concluido', label: 'Concluído', color: '#4caf50' },
    { value: 'vencido', label: 'Vencido', color: '#f44336' }
  ]

  const fetchReminders = useCallback(async () => {
    try {
      setLoading(true)
      const params = {
        searchParam,
        pageNumber: 1,
        status: statusFilter || undefined,
        documentType: documentTypeFilter || undefined,
        startDate: startDateFilter || undefined,
        endDate: endDateFilter || undefined
      }

      const { data } = await api.get('/reminders', { params })

      dispatch({ type: 'LOAD_REMINDERS', payload: data.reminders })
      setHasMore(data.hasMore)
      setLoading(false)
    } catch (err) {
      setLoading(false)
      toastError(err)
    }
  }, [
    searchParam,
    statusFilter,
    documentTypeFilter,
    startDateFilter,
    endDateFilter
  ])

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
  }, [
    searchParam,
    statusFilter,
    documentTypeFilter,
    startDateFilter,
    endDateFilter
  ])

  useEffect(() => {
    fetchReminders()
  }, [fetchReminders])

  const handleOpenReminderModal = () => {
    setSelectedReminder(null)
    setReminderModalOpen(true)
  }

  const handleCloseReminderModal = () => {
    setSelectedReminder(null)
    setReminderModalOpen(false)
  }

  const handleEditReminder = reminder => {
    setSelectedReminder(reminder)
    setReminderModalOpen(true)
  }

  const handleDeleteReminder = async reminderId => {
    try {
      await api.delete(`/reminders/${reminderId}`)
      toast.success('Lembrete excluído com sucesso!')
      dispatch({ type: 'DELETE_REMINDER', payload: reminderId })
    } catch (err) {
      toastError(err)
    }
    setDeletingReminder(null)
  }

  const handlePauseReminder = async reminder => {
    try {
      const response = await api.post(`/reminders/${reminder.id}/pause`)
      const isPaused = response.data.pausedAt !== null
      toast.success(
        isPaused
          ? 'Lembrete pausado com sucesso!'
          : 'Lembrete retomado com sucesso!'
      )
      fetchReminders()
    } catch (err) {
      toastError(err)
    }
  }

  const handleSearch = e => {
    setSearchParam(e.target.value.toLowerCase())
  }

  const handleOpenDeleteModal = reminder => {
    setDeletingReminder(reminder)
    setConfirmModalOpen(true)
  }

  const getReminderBaseDate = dueDate => {
    if (!dueDate) {
      return moment.invalid()
    }

    if (typeof dueDate === 'string') {
      // DATEONLY strings are parsed as local calendar dates.
      if (/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
        return moment(dueDate, 'YYYY-MM-DD', true)
      }

      // Datetime strings are converted to browser timezone before comparing by day.
      return moment.parseZone(dueDate).local().startOf('day')
    }

    return moment(dueDate).local().startOf('day')
  }

  const calculateNextExecution = reminder => {
    const recurrence = reminder.recurrence || reminder.repeat || 'none'
    if (recurrence === 'none') {
      return getReminderBaseDate(reminder.dueDate)
    }

    const now = moment().startOf('day')
    let nextDate = getReminderBaseDate(reminder.dueDate)

    // Se a data já passou, calcular a próxima execução baseada na recorrência
    while (nextDate.isBefore(now, 'day')) {
      switch (recurrence) {
        case 'monthly':
          nextDate.add(1, 'months')
          break
        case 'yearly':
          nextDate.add(1, 'years')
          break
        default:
          return getReminderBaseDate(reminder.dueDate)
      }
    }

    return nextDate
  }

  const formatDueDisplay = reminder => {
    const recurrence = reminder.recurrence || reminder.repeat || 'none'
    const baseDate = getReminderBaseDate(reminder.dueDate)

    if (!baseDate.isValid()) {
      return '-'
    }

    if (recurrence === 'monthly') {
      return `Mensal dia ${baseDate.format('D')}`
    }

    if (recurrence === 'yearly') {
      return `Anual ${baseDate.format('D/M')}`
    }

    return baseDate.format('DD/MM/YYYY')
  }

  // Returns the next date on which an action fires: earliest upcoming relative reminder,
  // or the due date itself when there are no relative reminders / all have already passed.
  const calculateNextReminderExecution = reminder => {
    const nextDue = calculateNextExecution(reminder)

    if (
      !reminder.relativeReminders ||
      reminder.relativeReminders.length === 0
    ) {
      return nextDue
    }

    const now = moment().startOf('day')
    const upcomingDates = reminder.relativeReminders
      .map(rel => moment(nextDue).add(Number(rel.days) || 0, 'days'))
      .filter(date => date.isSameOrAfter(now, 'day'))
      .sort((a, b) => a.valueOf() - b.valueOf())

    return upcomingDates.length > 0 ? upcomingDates[0] : nextDue
  }

  const getReminderWindow = reminder => {
    const nextExecution = calculateNextExecution(reminder)

    let windowStartDays = -30
    let windowEndDays = 0
    if (reminder.relativeReminders && reminder.relativeReminders.length > 0) {
      const allDays = reminder.relativeReminders.map(
        rel => Number(rel.days) || 0
      )
      windowStartDays = Math.min(-30, ...allDays)
      windowEndDays = Math.max(0, ...allDays)
    }

    return {
      startDate: moment(nextExecution).add(windowStartDays, 'days'),
      endDate: moment(nextExecution).add(windowEndDays, 'days')
    }
  }

  const isDateWithinReminderWindow = (reminder, date) => {
    const { startDate, endDate } = getReminderWindow(reminder)
    const referenceDate = moment(date)

    return (
      referenceDate.isSameOrAfter(startDate, 'day') &&
      referenceDate.isSameOrBefore(endDate, 'day')
    )
  }

  const isWithinRelativeReminders = reminder => {
    return isDateWithinReminderWindow(reminder, moment())
  }

  const getNonRecurringCompletionDate = reminder => {
    const dueDate = getReminderBaseDate(reminder.dueDate)
    if (!dueDate.isValid()) {
      return moment.invalid()
    }

    const maxRelativeDays = (reminder.relativeReminders || []).reduce(
      (maxDays, rel) => Math.max(maxDays, Number(rel.days) || 0),
      0
    )

    return moment(dueDate).add(maxRelativeDays, 'days').startOf('day')
  }

  const getDisplayStatus = reminder => {
    const recurrence = reminder.recurrence || reminder.repeat || 'none'
    const now = moment().startOf('day')
    const completionDate = getNonRecurringCompletionDate(reminder)

    if (
      recurrence === 'none' &&
      completionDate.isValid() &&
      now.isAfter(completionDate, 'day')
    ) {
      return { label: 'Concluído', value: 'concluido' }
    }

    if (reminder.status === 'closed' || reminder.status === 'concluido') {
      return { label: 'Concluído', value: 'concluido' }
    }

    if (reminder.status === 'pending' || reminder.status === 'pendente') {
      const nextExecution = calculateNextExecution(reminder)

      // Se está pausado
      if (
        reminder.pausedAt &&
        isDateWithinReminderWindow(reminder, reminder.pausedAt)
      ) {
        return { label: 'Suspenso', value: 'suspenso' }
      }

      // Se ainda não atingiu a próxima execução
      if (now.isBefore(nextExecution, 'day')) {
        return { label: 'Pendente', value: 'pendente' }
      }
    }

    return { label: 'Pendente', value: 'pendente' }
  }

  const getStatusIcon = reminder => {
    const displayStatus = getDisplayStatus(reminder)

    let IconComponent = ErrorIcon
    let className = 'pendente'

    if (displayStatus.value === 'concluido') {
      IconComponent = CheckCircleIcon
      className = 'concluido'
    } else if (displayStatus.value === 'suspenso') {
      IconComponent = PauseCircleFilledIcon
      className = 'suspenso'
    }

    return (
      <div className={classes.statusIcon}>
        <IconComponent className={classes[className]} />
        <Typography variant="caption">{displayStatus.label}</Typography>
      </div>
    )
  }

  const canPauseReminder = reminder => {
    return reminder.status === 'pending' && isWithinRelativeReminders(reminder)
  }

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1)
  }

  const handleScroll = e => {
    if (!hasMore || loading) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore()
    }
  }

  const customCellRenderer = {
    'cliente.nome': reminder => (
      <Typography variant="body2">
        {reminder.cliente?.nome || reminder.empresa || '-'}
      </Typography>
    ),
    contact: reminder => (
      <span>{reminder.contact?.name || reminder.contact?.number || '-'}</span>
    ),
    nextExecution: reminder =>
      calculateNextReminderExecution(reminder).format('DD/MM/YYYY'),
    dueDate: reminder => formatDueDisplay(reminder),
    status: reminder => getStatusIcon(reminder)
  }

  const handleDeleteClick = reminder => {
    setDeletingReminder(reminder)
    setConfirmModalOpen(true)
  }

  // Customizador de Ações (Ações complexas que o QuickTable padrão não cobre)
  const customActionsRenderer = reminder => (
    <div className={classes.containerActionsButton}>
      {canPauseReminder(reminder) && (
        <Tooltip title={reminder.pausedAt ? 'Retomar' : 'Pausar'}>
          <IconButton
            size="small"
            onClick={() => handlePauseReminder(reminder)}
            className={
              reminder.pausedAt ? classes.resumeButton : classes.pauseButton
            }
          >
            {reminder.pausedAt ? (
              <CirclePlay size={20} />
            ) : (
              <CirclePause size={20} />
            )}
          </IconButton>
        </Tooltip>
      )}
      <IconButton
        size="small"
        onClick={() => handleEditReminder(reminder)}
        className={classes.iconButtonEdit}
      >
        <Settings size={20} />
      </IconButton>
      <IconButton
        size="small"
        className={classes.iconButtonDelete}
        onClick={() =>
          setDeletingReminder(reminder) || setConfirmModalOpen(true)
        }
      >
        <Trash2 size={20} />
      </IconButton>
    </div>
  )

  return (
    <MainContainer>
      <ConfirmationModal
        title={`Excluir lembrete "${deletingReminder?.cliente?.nome || deletingReminder?.empresa || 'este lembrete'}"?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteReminder(deletingReminder.id)}
      >
        Tem certeza que deseja excluir este lembrete?
      </ConfirmationModal>

      <ReminderModal
        open={reminderModalOpen}
        onClose={handleCloseReminderModal}
        reminderId={selectedReminder?.id}
        onSave={() => {
          fetchReminders()
          handleCloseReminderModal()
        }}
      />

      <ReminderMessagesModal
        open={messagesModalOpen}
        onClose={() => setMessagesModalOpen(false)}
      />
      <MainHeader
        title="Lembretes"
        description="Gerencie todos os seus contatos, centralizando e organizando informações para facilitar o atendimento."
        // onTutorialClick={handleOpenTutorial}
        showTutorial
      >
        <MainHeaderButtonsWrapper>
          <ButtonSecondary
            label="Mensagens Pré-cadastradas"
            onClick={() => setMessagesModalOpen(true)}
          />
          <Button
            variant="contained"
            className={classes.buttonNewReminders}
            startIcon={<BellRing />}
            onClick={handleOpenReminderModal}
          >
            Novo Lembrete
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* <MainHeader>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setMessagesModalOpen(true)}
            style={{ marginRight: 8 }}
          >
            Mensagens Pré-cadastradas
          </Button>
          <Button variant="contained" color="primary">
            Novo Lembrete
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader> */}

      <Grid container className={classes.content}>
        <Grid item xs={3}>
          <InputFieldText
            className={classes.searchField}
            label="Pesquisar"
            name="pesquisar"
            placeholder="Pesquisar Lembrete"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid item xs={2}>
          <SelectInput
            value={documentTypeFilter}
            onChange={e => setDocumentTypeFilter(e.target.value)}
            label="Tipo de Documento"
          >
            <MenuItem value="">Todos</MenuItem>
            {documentTypes.map(type => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </SelectInput>
        </Grid>
        <Grid item xs={2}>
          <SelectInput
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="">Todos</MenuItem>
            {statusOptions.map(status => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </SelectInput>
        </Grid>
        <Grid item xs={1}>
          <InputFieldText
            className={classes.dateField}
            label="Data Inicial"
            type="date"
            variant="outlined"
            size="small"
            value={startDateFilter}
            onChange={e => setStartDateFilter(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
          />
        </Grid>
        <Grid item xs={1}>
          <InputFieldText
            className={classes.dateField}
            label="Data Final"
            type="date"
            value={endDateFilter}
            onChange={e => setEndDateFilter(e.target.value)}
          />
        </Grid>
      </Grid>

      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        {/* <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell>Contato</TableCell>
              <TableCell>Tipo de Documento</TableCell>
              <TableCell>Próxima Execução</TableCell>
              <TableCell>Vencimento</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reminders.map(reminder => (
              <TableRow key={reminder.id}>
                <TableCell>
                  {reminder.cliente?.nome || reminder.empresa || '-'}
                </TableCell>
                <TableCell>{reminder.responsible?.name || '-'}</TableCell>
                <TableCell>
                  {reminder.contact?.name || reminder.contact?.number || '-'}
                </TableCell>
                <TableCell>{reminder.documentType}</TableCell>
                <TableCell>
                  {calculateNextReminderExecution(reminder).format(
                    'DD/MM/YYYY'
                  )}
                </TableCell>
                <TableCell>{formatDueDisplay(reminder)}</TableCell>
                <TableCell align="center">{getStatusIcon(reminder)}</TableCell>
                <TableCell align="center">
                  <div className={classes.actionButtons}>
                    {canPauseReminder(reminder) && (
                      <Tooltip title={reminder.pausedAt ? 'Retomar' : 'Pausar'}>
                        <IconButton
                          size="small"
                          onClick={() => handlePauseReminder(reminder)}
                          className={
                            reminder.pausedAt
                              ? classes.resumeButton
                              : classes.pauseButton
                          }
                        >
                          {reminder.pausedAt ? (
                            <PlayCircleFilledIcon />
                          ) : (
                            <PauseCircleFilledIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleEditReminder(reminder)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteModal(reminder)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table> */}
        <QuickTable
          data={reminders}
          columns={columns}
          loading={loading}
          actions={['edit', 'delete']}
          onEdit={handleEditReminder}
          onDelete={handleDeleteClick}
          customActionsRenderer={customActionsRenderer}
          containerProps={{
            onScroll: handleScroll,
            className: classes.tableContainer // Use classes em vez de inline style para performance
          }}
          customCellRenderer={customCellRenderer}
          emptyMessage="Nenhum lembrete encontrado"
        />
      </Paper>
    </MainContainer>
  )
}

export default Reminders
